import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AppShell from "../../components/layout/AppShell";
import { createQuote, createTransaction, getTransactionById } from "../../services/api";
import { useSelector } from "react-redux";

// ── Country → local currency mapping ─────────────────────────────────────────
const COUNTRY_CURRENCY = {
  US: "USD",
  AE: "AED", ET: "ETB", GH: "GHS", KE: "KES", MW: "MWK",
  MZ: "MZN", NG: "NGN", RW: "RWF", ZA: "ZAR", TZ: "TZS",
  UG: "UGX", ZM: "ZMW", ZW: "ZWL",
};

// Seed USDC target used on mount to fetch the live rate
const SEED_TO_USDC = "1";

// ── Formatters ────────────────────────────────────────────────────────────────
// USDC: 7 decimal places
const fmtUsdc = (n) =>
  Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 7, maximumFractionDigits: 7 });

// Fiat: floor to 2 decimal places (half-round-down)
const fmtFiat = (n) => {
  const floored = Math.floor(Number(n ?? 0) * 100) / 100;
  return floored.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ── Debounce hook ─────────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ── Status helpers ────────────────────────────────────────────────────────────
const isSuccess = (s) => s === "completed" || s === "fulfilled";
const isFailed  = (s) => s === "failed" || s === "rejected" || s === "cancelled";
const isPending = (s) => !isSuccess(s) && !isFailed(s);

// ── Component ─────────────────────────────────────────────────────────────────
const FundWallet = () => {
  const navigate = useNavigate();
  const userId      = useSelector((s) => s.auth.userId);
  const authCountry = useSelector((s) => s.auth.country);
  const quotePermissions = useSelector((s) => s.auth.quotePermissions);

  useEffect(() => {
    if (!quotePermissions.canOnramp && !quotePermissions.canOfframp) {
      navigate("/sender/invoices", { replace: true });
    }
  }, [quotePermissions, navigate]);

  // Resolve sender's local currency from their onboarding country
  const localCurrency = COUNTRY_CURRENCY[(authCountry || "").toUpperCase()] || "NGN";

  // ── Rate state ────────────────────────────────────────────────────────────
  const [ratePerUsdc, setRatePerUsdc] = useState(null); // local currency per 1 USDC
  const [rateLoading, setRateLoading] = useState(true);
  const [rateError, setRateError]     = useState(false);

  // ── Amount entry ──────────────────────────────────────────────────────────
  const [usdcAmount, setUsdcAmount]   = useState("");
  const [amountError, setAmountError] = useState("");

  // ── Completion state ──────────────────────────────────────────────────────
  const [txn, setTxn]                     = useState(null);
  const [txnId, setTxnId]                 = useState(null);
  const [bankInfo, setBankInfo]           = useState(null);
  const [submittedUsdc, setSubmittedUsdc] = useState(0);   // snapshot at submit
  const [submittedLocal, setSubmittedLocal] = useState(0); // snapshot at submit
  const [submitting, setSubmitting]       = useState(false);

  // ── Step ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState(0);

  // ── Debounced USDC → local currency preview ───────────────────────────────
  const debouncedUsdc = useDebounce(usdcAmount, 500);
  const calculatedLocal =
    ratePerUsdc && debouncedUsdc && Number(debouncedUsdc) > 0
      ? Number(debouncedUsdc) * ratePerUsdc
      : null;

  // ── Seed quote on mount ───────────────────────────────────────────────────
  // Requests 1 USDC (toAmount) — the API returns the local-currency fromAmount
  // which is exactly the rate (local currency per 1 USDC). No back-calculation.
  const fetchSeedQuote = useCallback(async () => {
    if (!userId) return;
    setRateLoading(true);
    setRateError(false);
    try {
      const res = await createQuote({
        userId,
        fromCurrency: localCurrency,   // e.g. NGN
        toCurrency:   "USDC",
        toAmount:     SEED_TO_USDC,    // 1 USDC target → fromAmount = local per 1 USDC
        category:     "ONRAMP",
      });
      const q          = res.data;
      const drate      = Number(q?.drate ?? 0);
      const dsurcharge = Number(q?.dsurcharge ?? 0);
      if (drate > 0) {
        setRatePerUsdc(drate * (1 + dsurcharge / 100));
      } else {
        const fromAmt = Number(q?.fromAmount ?? q?.source_amount ?? 0);
        if (fromAmt > 0) setRatePerUsdc(fromAmt);
      }
    } catch {
      setRateError(true);
    } finally {
      setRateLoading(false);
    }
  }, [userId, localCurrency]);

  useEffect(() => { fetchSeedQuote(); }, [fetchSeedQuote]);

  // ── Transfer Fund ─────────────────────────────────────────────────────────
  const handleTransfer = async () => {
    const usdcVal = Number(usdcAmount);
    if (!usdcAmount || isNaN(usdcVal) || usdcVal <= 0) {
      setAmountError("Enter a valid USDC amount greater than 0.");
      return;
    }
    if (!ratePerUsdc) {
      setAmountError("Exchange rate not loaded. Please wait or retry.");
      return;
    }
    setAmountError("");
    setSubmitting(true);

    // Snapshot USDC immediately; local amount will be updated from locked quote
    setSubmittedUsdc(usdcVal);
    setSubmittedLocal(usdcVal * (ratePerUsdc ?? 0)); // pre-fill; overwritten below

    try {
      // 1. Lock final quote using toAmount (exact USDC target) — API returns the
      //    precise local-currency fromAmount, avoiding any rounding from back-calculation.
      const quoteRes = await createQuote({
        userId,
        fromCurrency: localCurrency,
        toCurrency:   "USDC",
        toAmount:     String(usdcVal), // exact USDC the user wants to receive
        category:     "ONRAMP",
      });
      const lockedQuote    = quoteRes.data;
      const lockedDrate    = Number(lockedQuote?.drate ?? 0);
      const lockedDsurch   = Number(lockedQuote?.dsurcharge ?? 0);
      if (lockedDrate > 0) {
        setSubmittedLocal(usdcVal * (lockedDrate * (1 + lockedDsurch / 100)));
      } else {
        const lockedLocal = Number(lockedQuote?.fromAmount ?? lockedQuote?.source_amount ?? 0);
        if (lockedLocal > 0) setSubmittedLocal(lockedLocal);
      }
      const quoteId = lockedQuote?.id;

      // 3. Create the on-ramp transaction — response contains bank transfer details
      const txnRes  = await createTransaction({ quoteId });
      const txnData = txnRes?.data ?? txnRes;
      const transactionId = txnData.id ?? txnData.transactionId;

      setTxnId(transactionId);
      // Bank details are in txnData.bank from createTransaction response
      if (txnData.bank) setBankInfo(txnData.bank);

      // 4. Optionally enrich with getTransactionById (non-blocking)
      try {
        const detailRes = await getTransactionById(transactionId);
        const detail    = detailRes?.data ?? detailRes;
        setTxn(detail);
        if (detail?.bank) setBankInfo(detail.bank);
      } catch { /* status detail is optional — proceed regardless */ }

      setStep(1);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived display helpers ───────────────────────────────────────────────
  const rateLabel = ratePerUsdc
    ? `${fmtFiat(ratePerUsdc)} ${localCurrency} = 1.00 USDC`
    : null;

  // Use snapshotted values — reliable regardless of getTransactionById response shape
  const txnUsdc  = submittedUsdc;
  const txnLocal = submittedLocal;
  const txnRef   = txn?.id ?? txnId ?? "—";
  const status   = txn?.status ?? "created";

  return (
    <AppShell>
      <div className="max-w-[600px]">

        {/* ── Step 0: Enter Amount ───────────────────────────────────────── */}
        {step === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Add funds via bank transfer
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Transfer {localCurrency} from your bank account. USDC is credited once we
              confirm receipt.
            </p>

            {/* You receive (USDC) — user input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                You receive (USDC)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                  ⓢ
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.0000001"
                  value={usdcAmount}
                  onChange={(e) => {
                    setUsdcAmount(e.target.value);
                    setAmountError("");
                  }}
                  placeholder="0.0000000"
                  className={`w-full border rounded-xl pl-8 pr-20 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand ${
                    amountError ? "border-red-400" : "border-gray-200"
                  }`}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400 pointer-events-none">
                  USDC
                </span>
              </div>
              {amountError && (
                <p className="mt-1 text-xs text-red-500">{amountError}</p>
              )}
            </div>

            {/* You send (local currency) — calculated */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                You send ({localCurrency})
              </label>
              <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 min-h-[46px]">
                {rateLoading ? (
                  <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                ) : calculatedLocal ? (
                  <span className="text-sm font-semibold text-gray-900">
                    {fmtFiat(calculatedLocal)}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400">—</span>
                )}
                <span className="text-xs font-semibold text-gray-400">{localCurrency}</span>
              </div>
            </div>

            {/* Exchange rate row */}
            <div className="flex items-center justify-between mb-6 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
                </svg>
                <span className="text-xs font-medium text-gray-500">Exchange rate</span>
              </div>
              {rateLoading ? (
                <div className="h-3.5 w-36 bg-gray-200 rounded animate-pulse" />
              ) : rateError ? (
                <button onClick={fetchSeedQuote} className="text-xs text-brand hover:underline">
                  Retry
                </button>
              ) : (
                <span className="text-xs font-semibold text-gray-900">{rateLabel}</span>
              )}
            </div>

            <button
              onClick={handleTransfer}
              disabled={submitting || rateLoading || rateError}
              className="w-full bg-brand text-white text-sm font-semibold py-3 rounded-lg hover:bg-brandHover transition-colors disabled:opacity-60"
            >
              {submitting ? "Processing…" : "Transfer Fund"}
            </button>
          </div>
        )}

        {/* ── Step 1: Complete (status-aware) ───────────────────────────── */}
        {step === 1 && (
          <div className="bg-white border border-gray-200 rounded-xl p-8 flex flex-col items-center text-center">

            {/* Status icon */}
            <div className="relative mb-6">
              {isFailed(status) ? (
                <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              ) : isSuccess(status) ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="absolute -top-2 -right-2 w-2 h-2 rounded-full bg-pink-400" />
                  <span className="absolute top-0 -left-3 w-1.5 h-1.5 rounded-full bg-yellow-400" />
                  <span className="absolute -bottom-1 right-0 w-1.5 h-1.5 rounded-full bg-orange-400" />
                  <span className="absolute -bottom-2 -left-1 w-1 h-1 rounded-full bg-pink-300" />
                </>
              ) : (
                <div className="w-16 h-16 rounded-full bg-orange-400 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Heading */}
            {isFailed(status) && (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Transfer Failed</h2>
                <p className="text-sm text-gray-500 mb-8">
                  Your transfer could not be processed. Please try again or contact support.
                </p>
              </>
            )}
            {isSuccess(status) && (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Transfer Complete!</h2>
                <p className="text-sm text-gray-500 mb-8">
                  Your {localCurrency} payment has been confirmed and USDC has been credited to your wallet.
                </p>
              </>
            )}
            {isPending(status) && (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Transfer Initiated</h2>
                <p className="text-sm text-gray-500 mb-8">
                  Make your {localCurrency} bank transfer using the details below. Your USDC
                  balance will be updated once we confirm receipt of your payment.
                </p>
              </>
            )}

            {/* Transaction summary */}
            <div className="w-full bg-gray-50 rounded-xl p-4 mb-4 space-y-3 text-left">
              {[
                ["You receive (USDC)",             fmtUsdc(txnUsdc)],
                [`You send (${localCurrency})`,    fmtFiat(txnLocal)],
                ["Status",                         status],
                ...(txnRef !== "—" ? [["Reference", txnRef]] : []),
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">{label}</span>
                  <span className={`text-sm font-medium ${
                    label === "Status"
                      ? isSuccess(status) ? "text-green-600"
                        : isFailed(status) ? "text-red-500"
                        : "text-orange-500"
                      : "text-gray-900"
                  }`}>
                    {value}
                  </span>
                </div>
              ))}
            </div>

            {/* Bank details (shown for pending/success) */}
            {!isFailed(status) && bankInfo && (
              <div className="w-full border border-gray-200 rounded-xl overflow-hidden mb-4 text-left">
                {[
                  ["Bank Name",       bankInfo.bankName],
                  ["Account Name",    bankInfo.accountHolderName],
                  ["Account No.",     bankInfo.accountNumber],
                  ["Routing Number",  bankInfo.routingNumber],
                  ["Payment Method",  bankInfo.paymentMethod],
                  ["SWIFT Code",      bankInfo.swiftCode],
                  ["Bank Address",    bankInfo.bankAddress],
                  ["Reference",       txnRef],
                ]
                  .filter(([, v]) => v)
                  .map(([label, value], i, arr) => (
                    <div
                      key={label}
                      className={`flex items-start justify-between px-4 py-3 bg-white ${
                        i < arr.length - 1 ? "border-b border-gray-100" : ""
                      }`}
                    >
                      <span className="text-sm text-gray-500 w-36 flex-shrink-0">{label}</span>
                      <span className="text-sm font-semibold text-gray-900 text-right break-all">{value}</span>
                    </div>
                  ))}
              </div>
            )}

            {/* Copy reference */}
            {!isFailed(status) && txnRef !== "—" && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(txnRef);
                  toast.success("Reference copied");
                }}
                className="mb-8 flex items-center gap-1.5 text-xs text-brand hover:underline"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy Reference
              </button>
            )}

            {/* CTAs */}
            <div className="flex gap-3">
              {isFailed(status) ? (
                <>
                  <button
                    onClick={() => setStep(0)}
                    className="px-6 py-2.5 text-sm font-medium text-brand border border-brand rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate("/sender/treasury")}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brandHover transition-colors"
                  >
                    Back to Treasury
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate("/sender/treasury")}
                    className="px-6 py-2.5 text-sm font-medium text-brand border border-brand rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Back to Treasury
                  </button>
                  <button
                    onClick={() => navigate("/sender/transactions")}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brandHover transition-colors"
                  >
                    View Transactions
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default FundWallet;
